import { ArrowBack, Close } from '@mui/icons-material';
import { Button, Container, Icon, IconButton, lighten, Skeleton, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import settings from '/@/core/settings';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { objectEntries } from '/@/utils';

export type NewFieldOption = 'draw' | 'upload' | 'import';
const NewFieldOptionsSelector = ({
    onButtonClick,
    showOptions,
    userNodeId,
}: {
    onButtonClick: (selectedOption: NewFieldOption) => void;
    showOptions: NewFieldOption[];
    userNodeId: number | undefined;
}) => {
    const userNodeQuery = useGetUserNode(userNodeId);

    const options: Record<
        NewFieldOption,
        {
            title: string;
            description: string;
            icon: string;
        }
    > = {
        draw: {
            title: _('Dibujar un lote'),
            description: _('Puedes dibujar un lote o marcar un punto en el mapa'),
            icon: 'drive_file_rename_outline',
        },
        upload: {
            title: _('Subir un archivo'),
            description: _('Puedes subir un archivo con el formato .kmz, .kml, .geojson o .shp. '),
            icon: 'upload_file',
        },
        import: {
            title: _('Conectarme con Fieldview'),
            description: _('Puedes conectarte con Fieldview y sincronzar tus lotes en {{appName}}', {
                appName: settings.app.name,
            }),
            icon: 'link',
        },
    } as const;

    const [selectedOption, setSelectedOption] = useState<NewFieldOption>();
    const navigate = useNavigate();

    return (
        <Container
            maxWidth='sm'
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
            }}
        >
            <Stack sx={{ background: 'white', border: (t) => `1px solid ${t.palette.divider}`, borderRadius: '4px' }}>
                <Stack
                    sx={{
                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    }}
                    alignItems={'center'}
                    p={2}
                    direction={'row'}
                    justifyContent={'space-between'}
                >
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Typography fontWeight={500} fontSize={'20px'}>
                        {userNodeId ? (
                            userNodeQuery.isValidating ? (
                                <Skeleton variant='text' width={100} height={20} />
                            ) : (
                                _('Crear un nuevo lote para el productor: {{name}}', { name: userNodeQuery.data?.name })
                            )
                        ) : (
                            _('Crear un nuevo lote')
                        )}
                    </Typography>
                    <IconButton onClick={() => navigate(-1)}>
                        <Close />
                    </IconButton>
                </Stack>

                <Stack alignItems={'center'} m={2}>
                    <Typography fontWeight={400} fontSize={'14px'}>
                        {_('Seleccione una opción')}
                    </Typography>
                </Stack>

                <Stack spacing={2} mx={{ xs: 2, sm: 10 }} mb={2} mt={0}>
                    {objectEntries(options)
                        .filter(([key]) => showOptions.includes(key))
                        .map(([key, { title, description, icon }]) => (
                            <button onClick={() => setSelectedOption(key)} key={key}>
                                <Stack
                                    direction={'row'}
                                    sx={[
                                        {
                                            border: (t) => `1px solid ${t.palette.divider}`,
                                            borderRadius: '4px',
                                            ':hover': {
                                                background: (t) => lighten(t.palette.secondary.main, 0.95),
                                                borderColor: (t) => lighten(t.palette.secondary.main, 0.5),
                                            },
                                        },
                                        selectedOption === key && {
                                            background: (t) => lighten(t.palette.secondary.main, 0.85),
                                            borderColor: (t) => t.palette.secondary.main,
                                            ':hover': {
                                                background: (t) => lighten(t.palette.secondary.main, 0.85),
                                            },
                                        },
                                    ]}
                                    alignItems={'center'}
                                    textAlign={'start'}
                                    spacing={2}
                                    p={2}
                                >
                                    <Icon color='primary' sx={{ fontSize: '40px' }}>
                                        {icon}
                                    </Icon>
                                    <Stack>
                                        <Typography fontWeight={500} fontSize={'20px'}>
                                            {title}
                                        </Typography>
                                        <Typography fontWeight={400} fontSize={'14px'}>
                                            {description}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </button>
                        ))}
                </Stack>

                <Stack
                    direction={'row'}
                    justifyContent={'flex-end'}
                    sx={{
                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                    }}
                    alignItems={'center'}
                    p={2}
                >
                    <Button
                        color='secondary'
                        variant='contained'
                        onClick={() => onButtonClick(selectedOption!)}
                        disabled={!selectedOption}
                    >
                        {_('Aceptar')}
                    </Button>
                </Stack>
            </Stack>
        </Container>
    );
};

export default NewFieldOptionsSelector;
